import { createContext, useContext, type ReactNode } from 'react'

import type { AIModel } from '@/api/types'
import type { StudioNodeData } from '@/features/studio/canvas/types'

export interface CanvasNodeContextValue {
  aspectRatio: string
  imageModels: AIModel[]
  videoModels: AIModel[]
  audioModels: AIModel[]
  textModels: AIModel[]
  onRun: (
    nodeId: string,
    opts?: {
      textInstruction?: string
      promptPositive?: string
      promptNegative?: string
      genParams?: Record<string, string | number>
    },
  ) => void
  /** 轻量 patch：不触发全图 upstream 重算，供外置输入框草稿同步 */
  onPatchDraft: (nodeId: string, patch: Partial<StudioNodeData> | ((data: StudioNodeData) => Partial<StudioNodeData>)) => void
  onPatch: (
    nodeId: string,
    patch: Partial<StudioNodeData> | ((data: StudioNodeData) => Partial<StudioNodeData>),
  ) => void
  onFocusUpstream: (nodeId: string) => void
  onPreferTake: (nodeId: string, version: number, audioTab?: import('@/features/studio/canvas/types').AudioProductTab) => void
  /** 上传文件到 upload / ref_* 节点；kind 限制仅接受某类（如 'image'） */
  onUploadFile: (nodeId: string, file: File | null, kind?: 'image' | 'video' | 'audio') => void
  /** 导出节点：上游视频 + 音频合成；仅视频时采用上游成片 */
  onExportCompose?: (nodeId: string) => void | Promise<void>
  /** 自由创作：从合成导出打开同步/发布 */
  isCreative?: boolean
  onOpenPublishWorks?: (exportNodeId: string) => void
  /** 音频截取：从当前预览截取一段，新建 upload 节点 */
  onTrimAudio?: (nodeId: string, startSec: number, endSec: number) => void | Promise<void>
  /** 组节点缩放 / 尺寸变更后触发（持久化） */
  onNodeGeometryChange?: () => void
  /** 删除连线（无权限时为 undefined） */
  onDeleteEdge?: (edgeIds: string[]) => void
}

const CanvasNodeContext = createContext<CanvasNodeContextValue | null>(null)

export function CanvasNodeProvider({
  value,
  children,
}: {
  value: CanvasNodeContextValue
  children: ReactNode
}) {
  return <CanvasNodeContext.Provider value={value}>{children}</CanvasNodeContext.Provider>
}

export function useCanvasNodeContext(): CanvasNodeContextValue {
  const ctx = useContext(CanvasNodeContext)
  if (!ctx) throw new Error('useCanvasNodeContext must be used within CanvasNodeProvider')
  return ctx
}
